import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Starfield } from "../components/Starfield";
import { Header } from "../components/Header";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { BookMarked, Flame, MessageCircle } from "lucide-react";

const PLAN_LABEL = { free: "Seeker (Free Trial)", ascendent: "Ascendent · Monthly", luminous: "Luminous · Annual" };

export default function Account() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState({ sessions: 0, insights: 0 });

  useEffect(() => {
    const load = async () => {
      try {
        const [s, j] = await Promise.all([api.get("/sessions"), api.get("/journal")]);
        setStats({ sessions: s.data.length, insights: j.data.length });
      } catch {}
    };
    load();
  }, []);

  if (!user || !user.user_id) return null;

  const trialEnds = user.trial_ends_at ? new Date(user.trial_ends_at).toLocaleDateString() : "—";

  const statCards = [
    { icon: MessageCircle, label: "Sessions", value: stats.sessions },
    { icon: BookMarked, label: "Insights saved", value: stats.insights },
    { icon: Flame, label: "Current plan", value: (PLAN_LABEL[user.plan] || "Seeker").split(" ")[0] },
  ];

  return (
    <div className="relative min-h-screen bg-cg-bg">
      <Starfield />
      <Header />
      <section className="relative z-10 max-w-4xl mx-auto px-5 pt-32 pb-24">
        <h1 className="font-serif text-3xl sm:text-4xl font-extralight tracking-tight mb-2">Your ascension</h1>
        <p className="text-cg-texts mb-6">{user.name} · {user.email}</p>
        <Button
          data-testid="account-metrics-button"
          onClick={() => navigate("/metrics")}
          variant="outline"
          className="mb-10 rounded-full bg-transparent border-[rgba(92,200,240,0.4)] text-cg-cyan hover:bg-[rgba(92,200,240,0.08)]"
        >
          View full ascension metrics
        </Button>

        <div className="grid sm:grid-cols-3 gap-5 mb-10">
          {statCards.map((c) => (
            <div key={c.label} className="rounded-2xl p-6 bg-cg-card border border-[rgba(140,79,214,0.22)]">
              <c.icon className="h-5 w-5 text-cg-cyan mb-3" />
              <p className="font-serif text-3xl text-cg-textp">{c.value}</p>
              <p className="text-sm text-cg-textm mt-1">{c.label}</p>
            </div>
          ))}
        </div>

        <div className="rounded-2xl p-7 cg-glass">
          <h2 className="font-serif text-xl text-cg-textp mb-4">Plan & billing</h2>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-cg-textp font-medium" data-testid="account-plan-name">{PLAN_LABEL[user.plan] || "Seeker (Free Trial)"}</p>
              <p className="text-sm text-cg-textm mt-1">
                {user.plan === "free" ? `Trial ends ${trialEnds}` : `Status: ${user.plan_status || "active"}`}
              </p>
            </div>
            <Button
              data-testid="account-upgrade-button"
              onClick={() => navigate("/pricing")}
              className="rounded-full bg-cg-violet hover:bg-cg-violetglow text-white"
            >
              {user.plan === "free" ? "Upgrade plan" : "Change plan"}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
